import React from 'react';
import { Box, FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { UploadDropzone, UploadPreviewRow } from '../../../ui/UploadDropzone';

interface FileInputFormProps {
  defaultVal?: any;
  name: string | undefined | any;
  label: string | undefined;
  isRequired?: boolean;
  register: UseFormRegister<FieldValues>;
  watch: any;
  errors: any;
  accept?: string;
  description?: string;
  compact?: boolean;
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileNameFromUrl = (url: string) => {
  try {
    const path = new URL(url).pathname;
    return decodeURIComponent(path.split('/').pop() || 'arquivo');
  } catch {
    return 'arquivo';
  }
};

export const FileInputForm = ({
  defaultVal,
  name,
  label,
  isRequired,
  register,
  watch,
  errors,
  accept = 'image/png, image/jpeg, image/jpg, audio/mp3, video/mp4, application/pdf',
  description = 'PDF, imagem, áudio ou vídeo',
  compact = false,
}: FileInputFormProps) => {
  const selected = watch(name);
  const file: File | undefined =
    selected && typeof selected === 'object' && selected.length > 0 ? selected[0] : undefined;
  const hasError = !!errors[name];

  const dropFiles = (files: FileList) => {
    const input = document.getElementById(name) as HTMLInputElement | null;
    if (!input) return;
    input.files = files;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  let content = (
    <UploadDropzone
      inputId={name}
      title="Clique para enviar"
      onFiles={dropFiles}
      description={description}
      isInvalid={hasError}
      compact={compact}
    />
  );
  if (file) {
    content = (
      <UploadPreviewRow
        name={file.name}
        meta={`${formatSize(file.size)} · ${file.type.split('/').pop()?.toUpperCase() ?? ''}`}
        isImage={false}
        replaceInputId={name}
      />
    );
  } else if (defaultVal) {
    content = (
      <UploadPreviewRow
        name={fileNameFromUrl(defaultVal)}
        meta="Arquivo atual"
        isImage={false}
        replaceInputId={name}
        onOpen={() => window.open(defaultVal, '_blank', 'noopener,noreferrer')}
      />
    );
  }

  return (
    <FormControl isRequired={isRequired} isInvalid={hasError}>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      {content}
      <Box as="input" id={name} type="file" hidden accept={accept} {...register(name)} />
      {hasError && <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>}
    </FormControl>
  );
};
